
import { supabase } from "@/integrations/supabase/client";
import { EmailTemplate } from "@/types/emailTemplates";

/**
 * Fetch a single email template by ID
 */
export async function fetchTemplate(id: string): Promise<EmailTemplate | null> {
  try {
    console.log(`Fetching template with ID: ${id}`);
    
    // Verify authentication
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      console.error("No auth session found while fetching template");
      throw new Error("Authentication required to fetch template");
    }
    
    // Maximum retry attempts
    const maxRetries = 3;
    let retryCount = 0;
    
    // Wrapper function that implements retrying
    const executeWithRetry = async (): Promise<EmailTemplate | null> => {
      try {
        console.log(`Executing template fetch for ID ${id}, attempt ${retryCount + 1}`);
        
        // Use any type to bypass TypeScript's type checking
        const { data, error } = await supabase
          .from('email_templates')
          .select('*')
          .eq('id', id)
          .single();

        if (error) {
          throw new Error(`Error fetching template: ${error.message}`);
        }

        if (!data) {
          throw new Error(`Template with ID ${id} not found in database`);
        }
        
        console.log(`Successfully fetched template "${data.name || 'Unnamed'}"`);
        return data as EmailTemplate;
      } catch (error: any) {
        console.error(`Fetch attempt ${retryCount + 1} failed:`, error);
        
        // If we have retries left, and it's a potentially transient error
        if (retryCount < maxRetries && 
            (error.message?.includes('network') || 
             error.message?.includes('timeout') || 
             error.status === 500 ||
             error.status === 503)) {
          
          retryCount++;
          console.log(`Retrying fetchTemplate (${retryCount}/${maxRetries})...`);
          
          // Exponential backoff
          const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
          await new Promise(resolve => setTimeout(resolve, delay));
          
          return executeWithRetry();
        }
        
        // Otherwise, rethrow the error
        throw error;
      }
    };
    
    return await executeWithRetry();
  } catch (error: any) {
    console.error("Error in fetchTemplate:", error);
    throw new Error(`Failed to fetch email template: ${error.message}`);
  }
}
